import { IconChevronLeft16 } from '@warp-ds/icons/react/chevron-left-16';
import { IconChevronRight16 } from '@warp-ds/icons/react/chevron-right-16';
import cn from 'classnames';
import { type CSSProperties, memo } from 'react';
import { useCarouselDots } from './hooks/useCarouselDots';
import { useCarouselNavigation } from './hooks/useCarouselNavigation';
import { useImageLoading } from './hooks/useImageLoading';
import styles from './ImageCarousel.module.css';
import { DOT_SIZE_REM, DOT_SPACING_REM } from './utils/constants';
import type { CarouselStyles, ImageCarouselProps } from './utils/types';

export type { ImageCarouselProps } from './utils/types';

export const ImageCarousel = memo(({ images, alt, className, onImageChange, initialIndex = 0, loading = 'lazy' }: ImageCarouselProps) => {
    const { displayableImages, handleImageLoadError } = useImageLoading(images);
    const totalImages = displayableImages.length;

    const { activeImageIndex, showControls, showPrevButton, showNextButton, handleNavigationClick } = useCarouselNavigation({
        initialIndex,
        totalImages,
        onImageChange,
    });

    const { dotsTrackOffsetRem, edgeDotIndices } = useCarouselDots({
        totalImages,
        activeImageIndex,
    });

    if (!totalImages) {
        return null;
    }

    const carouselStyles: CarouselStyles & CSSProperties = {
        '--dot-size': `${DOT_SIZE_REM}rem`,
        '--dot-spacing': `${DOT_SPACING_REM}rem`,
    };

    return (
        <div className={cn(styles.carousel, className)} style={carouselStyles} data-testid="image-carousel">
            <div className={styles.imageTrack} style={{ transform: `translateX(-${activeImageIndex * 100}%)` }}>
                {displayableImages.map(({ image, originalIndex }, index) => (
                    <img
                        key={originalIndex}
                        src={image}
                        alt={`${alt ?? ''} ${index + 1} av ${totalImages}`}
                        className={styles.image}
                        loading={index === activeImageIndex ? loading : 'lazy'}
                        onError={() => handleImageLoadError(originalIndex)}
                    />
                ))}
            </div>
            {showControls && (
                <>
                    {showPrevButton && (
                        <button
                            type="button"
                            onClick={handleNavigationClick(-1)}
                            className={cn(styles.navButton, styles.navButtonPrev)}
                            aria-label="Forrige bilde"
                            data-testid="carousel-prev">
                            <IconChevronLeft16 aria-hidden />
                        </button>
                    )}
                    {showNextButton && (
                        <button
                            type="button"
                            onClick={handleNavigationClick(1)}
                            className={cn(styles.navButton, styles.navButtonNext)}
                            aria-label="Neste bilde"
                            data-testid="carousel-next">
                            <IconChevronRight16 aria-hidden />
                        </button>
                    )}
                    <div className={styles.indicators}>
                        <div className={styles.dotsTrack} style={{ transform: `translateX(${dotsTrackOffsetRem}rem)` }}>
                            {displayableImages.map(({ originalIndex }, index) => (
                                <span
                                    key={originalIndex}
                                    data-testid={`carousel-dot-${index}`}
                                    className={cn(styles.dot, {
                                        [styles.dotTiny]: edgeDotIndices.has(index),
                                        [styles.dotActive]: index === activeImageIndex,
                                    })}
                                />
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
});

ImageCarousel.displayName = 'ImageCarousel';
